/**
 * components/TxnRowActions.js
 * Shared Aksi cluster (edit · [bayar] · "···" more-actions dropdown) — identical between
 * Penjualan, Pembelian, and Piutang & Utang's transaction tables. Duplikat was removed in T1f
 * (never requested; it came from the PRD's action cluster, not an actual product ask).
 *
 * Phase T1g: compact icon-only buttons (BukuKas-style) instead of bordered icon+label
 * buttons — each still carries a `title` (hover tooltip) AND `aria-label` (screen readers;
 * `title` alone isn't reliably exposed as an accessible name) so the action is never a
 * guessing game, and stays a real `<button>` so it's keyboard-reachable/activatable for free.
 *
 * Phase T2b-fix: removed the history-clock button (`onToggleHistory`) and its inline
 * row-expansion — it duplicated `TransactionDetailModal`'s own "Riwayat Perubahan" card, which
 * shows the identical `EditLogTimeline` and is already one row-click away. `ClockIcon` was
 * removed from `Icons.js` alongside it (no longer referenced anywhere).
 *
 * Phase T2c-1: added an OPTIONAL "Cetak Invoice Pusat" button (`onPrint`).
 *
 * **Phase T2c-2**: `onPrint` renamed `onPrintInvoicePusat`, joined by two more OPTIONAL print
 * callbacks — `onPrintSuratJalanPusat`/`onPrintChecklist` — each independently
 * omitted-means-no-button (not a disabled one), same convention as the original. `Pembelian.js`
 * never passes any of these five (PRD: "Pembelian prints nothing"). The "Pengepakan" button
 * (`onPackaging`) is GONE — `PengepakanModal` was retired this same session; its fields now
 * live inside the Surat Jalan / Checklist print dialogs themselves, reached by clicking the
 * matching Cetak button. `PackageIcon` was removed from `Icons.js` alongside it (grepped to
 * confirm no other reference survived).
 *
 * **Phase T2d**: two more OPTIONAL print callbacks — `onPrintInvoiceCabang`/
 * `onPrintSuratJalanCabang` — same independently-optional convention. `Penjualan.js` gates the
 * three PUSAT print actions on `t.location === "PUSAT"` and these two CABANG ones on
 * `t.location === "CABANG"`, so a given row shows only the actions for its own anchor, never a
 * mix of both.
 *
 * **Icon/color differentiation fix**: all five Cetak buttons used to share one `PrinterIcon`,
 * indistinguishable from each other. Replaced with three distinct icons + color modifiers,
 * grouped by DOCUMENT, not by pusat/cabang — Invoice Pusat/Cabang share `ReceiptIcon` (blue),
 * Surat Jalan Pusat/Cabang share `TruckIcon` (teal), Checklist gets its own
 * `ClipboardCheckIcon` (purple) — safe to reuse one icon+color between a document's pusat and
 * cabang variant since the two are mutually exclusive per row (gated by `t.location` above),
 * never shown together. `PrinterIcon` was removed from `Icons.js`.
 *
 * **Phase T3 (Bayar Tagihan)**: one more OPTIONAL button, `onBayarTagihan` — the FIRST one in
 * this cluster gated on transaction *status*, not `location` like every `onPrint*` callback
 * above. Both `Penjualan.js`/`Pembelian.js` pass it only when `txnStatus(t) !== "LUNAS"` (Sisa
 * Tagihan > 0); a Lunas row never shows it. Placed right after Ubah, before the "···" cluster —
 * a money action grouped near edit, everything else after. Its icon color follows
 * `TxnNilaiCell`'s own `txn.type` convention (green PENJUALAN, red PEMBELIAN, blue-deep
 * TRANSFER) rather than one fixed color, via the required-when-`onBayarTagihan`-is-present
 * `txnType` prop.
 *
 * **Aksi column consolidation (Brief 2 of the sidebar/dashboard redesign set)**: the five
 * Cetak buttons and Hapus — previously a flat run of up to 5 more icon buttons — are now
 * behind a single "···" (Aksi Lainnya) trigger that opens a dropdown menu, each item showing
 * an icon AND a text label instead of icon-only. Ubah and Bayar Tagihan are UNCHANGED — same
 * position, same always-visible click behavior. This is a pure interaction/layout change: the
 * component's props are byte-identical to before, every action still calls the exact same
 * callback prop it always has, and Hapus still just calls `onDelete` (the actual confirm step
 * lives in the caller's own `DeleteConfirmModal`, untouched by this component). Menu item
 * labels are deliberately SHORTENED from their old Pusat/Cabang-suffixed aria-label/title text
 * ("Cetak Invoice Pusat"/"Cetak Invoice Cabang" → "Cetak Invoice", etc.) — confirmed with
 * Chalton: since `onPrintInvoicePusat`/`onPrintInvoiceCabang` (and the Surat Jalan pair) are
 * mutually exclusive per row, the Pusat/Cabang suffix is redundant once you're looking at that
 * specific row's own menu, and repeating it there is noise, not clarity.
 *
 * The menu itself is `position: fixed`, not `position: absolute` — deliberately: `.table-wrap`
 * (every table in this app, including this cluster's own host tables) sets `overflow-x: auto`,
 * which per the CSS spec also forces `overflow-y`'s computed value away from `visible`, so an
 * absolutely-positioned popover anchored inside a table row would be silently clipped for any
 * row near the table's bottom edge (the same class of bug `KelolaKategoriModal`'s own tooltip
 * had to route around via a scoped `overflow-x: visible` override — not repeated here since
 * that would cost every table its horizontal-scroll capability on a narrow viewport). A `fixed`
 * element's containing block is the viewport, not the nearest positioned ancestor, so it
 * escapes that clipping with no portal needed (confirmed no ancestor of a table row sets
 * `transform`/`filter`/`will-change: transform`, any of which would otherwise trap a `fixed`
 * descendant) — position is computed once at open time from the trigger's own
 * `getBoundingClientRect()`. Outside-click-to-close reuses `Combobox.js`'s own established
 * capture-phase `document.addEventListener("mousedown", fn, true)` mechanism. Escape follows
 * Combobox's own "busy-Esc" convention (only swallows+`stopPropagation()`s while open) but is
 * bound as a plain React `onKeyDown` on this component's own wrapping `<span>` — NOT a second
 * document-level listener — since a document-level listener would race `useModalEsc`'s OWN
 * document-level listener by pure registration order whenever an event's target happens to be
 * `document` itself (both non-capture listeners on the same node collapse to registration
 * order there), which isn't the real bubble path a genuine keypress takes. Binding on this
 * wrapper instead means Escape is intercepted at this widget's own DOM position — before it
 * ever continues bubbling up to `document` — the same guarantee Combobox.js gets from binding
 * onKeyDown directly to its own input. The menu also closes on window scroll/resize (a `fixed`
 * menu doesn't track the trigger if the underlying page scrolls) and after any item is clicked.
 */
import React, { useEffect, useRef, useState } from "react";
import { PencilIcon, TrashIcon, ReceiptIcon, TruckIcon, ClipboardCheckIcon, WalletIcon, MoreIcon } from "./Icons";

const PAYMENT_COLOR_CLASS = {
  PENJUALAN: "icon-btn--payment-in",
  PEMBELIAN: "icon-btn--payment-out",
  TRANSFER: "icon-btn--payment-transfer",
};

export default function TxnRowActions({
  onEdit, onBayarTagihan, txnType, onPrintInvoicePusat, onPrintSuratJalanPusat, onPrintChecklist,
  onPrintInvoiceCabang, onPrintSuratJalanCabang, onDelete,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // { top, right } — fixed-position coords
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const closeMenu = () => setMenuOpen(false);
  const openMenu = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: Math.max(0, window.innerWidth - rect.right) });
    setMenuOpen(true);
  };
  const runAction = (fn) => { fn(); closeMenu(); };

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      closeMenu();
    };
    const onScrollOrResize = () => closeMenu();
    document.addEventListener("mousedown", onDown, true);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [menuOpen]);

  // Escape handling lives on this wrapper's own onKeyDown (a React bubble handler rooted at
  // the trigger/menu themselves), not a document-level listener — a document-level listener
  // would race useModalEsc's OWN document-level listener by pure registration order (both are
  // non-capture listeners on the same node once an event's target IS document), which is not
  // the real-world bubble path a genuine keypress takes. Binding here instead means Escape is
  // handled at this widget's own DOM position, before it ever continues bubbling up to
  // document — the same "stop it before it reaches the host modal's listener" guarantee
  // Combobox.js gets from binding onKeyDown directly to its own input. Mirrors Combobox's own
  // busy-Esc convention: only swallow+stopPropagation while the menu is actually open.
  const onWrapperKeyDown = (e) => {
    if (e.key === "Escape" && menuOpen) { closeMenu(); e.stopPropagation(); }
  };

  return (
    <span className="icon-actions" onKeyDown={onWrapperKeyDown}>
      <button type="button" className="icon-btn" onClick={onEdit} title="Ubah" aria-label="Ubah">
        <PencilIcon />
      </button>
      {onBayarTagihan && (
        <button
          type="button"
          className={`icon-btn ${PAYMENT_COLOR_CLASS[txnType] || ""}`}
          onClick={onBayarTagihan}
          title="Bayar Tagihan"
          aria-label="Bayar Tagihan"
        >
          <WalletIcon />
        </button>
      )}
      <button
        type="button"
        ref={triggerRef}
        className="icon-btn"
        onClick={() => (menuOpen ? closeMenu() : openMenu())}
        title="Aksi Lainnya"
        aria-label="Aksi Lainnya"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <MoreIcon />
      </button>
      {menuOpen && menuPos && (
        <div
          ref={menuRef}
          role="menu"
          className="row-actions-menu"
          style={{ top: menuPos.top, right: menuPos.right }}
        >
          {(onPrintInvoicePusat || onPrintInvoiceCabang) && (
            <button
              type="button" role="menuitem"
              className="row-actions-menu__item row-actions-menu__item--invoice"
              onClick={() => runAction(onPrintInvoicePusat || onPrintInvoiceCabang)}
            >
              <ReceiptIcon /><span>Cetak Invoice</span>
            </button>
          )}
          {(onPrintSuratJalanPusat || onPrintSuratJalanCabang) && (
            <button
              type="button" role="menuitem"
              className="row-actions-menu__item row-actions-menu__item--suratjalan"
              onClick={() => runAction(onPrintSuratJalanPusat || onPrintSuratJalanCabang)}
            >
              <TruckIcon /><span>Cetak Surat Jalan</span>
            </button>
          )}
          {onPrintChecklist && (
            <button
              type="button" role="menuitem"
              className="row-actions-menu__item row-actions-menu__item--checklist"
              onClick={() => runAction(onPrintChecklist)}
            >
              <ClipboardCheckIcon /><span>Cetak Checklist</span>
            </button>
          )}
          <button
            type="button" role="menuitem"
            className="row-actions-menu__item row-actions-menu__item--danger"
            onClick={() => runAction(onDelete)}
          >
            <TrashIcon /><span>Hapus</span>
          </button>
        </div>
      )}
    </span>
  );
}
